/**
 * Quality Co-Pilot investigations API service.
 *
 * Jess's "case files": an investigation tracks a quality problem from
 * Investigating → Remediating → Completed (with reopen/cancel side paths),
 * bundling the conversations and data requests that belong to it. This module
 * wraps the door-scoped endpoints under `/api/quality-copilot/investigations`
 * that the Quality Co-Pilot page uses to list investigations, save/attach
 * conversations, and walk the status FSM.
 *
 * Uses the shared axios instance (services/api.ts) like qcpDataRequests.ts.
 * Every endpoint 404s when the deploy has the feature flagged off — callers
 * treat a 404 on the list as "surface unavailable" and hide the whole UI.
 */
import api from './api';
import type { DoorDataRequestRead } from './qcpDataRequests';

const BASE = '/quality-copilot';

export type InvestigationStatus =
  | 'investigating'
  | 'remediating'
  | 'completed'
  | 'cancelled';

/** Whose court the ball is in — orthogonal to the lifecycle status. */
export type CaseWorkState =
  | 'outstanding_on_us'
  | 'waiting_on_customer'
  /** A teammate owes the next move (the lab, CS, the warehouse). Servers
   *  from before it never offer it in allowed_work_states. */
  | 'waiting_on_colleague'
  | 'ready_to_close';

/** Whose desk an outstanding checklist item sits on. */
export type CaseTaskOwner = 'us' | 'colleague' | 'customer';

export type CaseTaskStatus = 'open' | 'done' | 'cancelled';

/** One outstanding checklist item on a case (DoorCaseTaskRead). */
export interface DoorCaseTaskRead {
  id: string;
  label: string;
  owner: CaseTaskOwner;
  status: CaseTaskStatus;
  position: number;
  /** The FSM's legal next statuses — controls derive from this ONLY. */
  allowed_statuses: string[];
  /** Sigma stage 2: the named person behind the desk and the completion
   *  date (YYYY-MM-DD) they committed to. Optional: older servers omit them. */
  owner_name?: string | null;
  due_at?: string | null;
  /** Server-judged: open, dated, and the date has passed. */
  overdue?: boolean;
  /** Jess's words when he removed it (kanban 59a1ee04); null on a plain
   *  remove or once restored. Older servers omit it. */
  cancelled_note?: string | null;
  done_at: string | null;
  /** Who checked it off and the fact that settled it; `done_in_chat` when
   *  the user told Merlin and Merlin ticked it (kanban 101a9853). Optional:
   *  older servers omit them. */
  done_by_label?: string | null;
  done_note?: string | null;
  done_in_chat?: boolean;
  /** How a customer-owed step reaches the customer (kanban d3915b86): the
   *  editor's wording, or null for the default "through the account's sales
   *  rep". Never sent; shown where "Ask the customer" used to be. Optional:
   *  older servers omit it. */
  recommendation?: string | null;
  created_at: string;
  updated_at: string;
}

/** A Sigma SLA clock's reading (services/investigations/sla.py). */
export type SlaClockKey =
  | 'issue_identified'
  | 'root_cause'
  | 'corrective_action'
  | 'customer_acknowledged'
  | 'customer_response'
  | 'effectiveness';

export type SlaClockStatus =
  | 'on_track'
  | 'due_soon'
  | 'overdue'
  | 'met'
  | 'missed'
  | 'watching'
  | 'held'
  | 'failed'
  | 'inactive';

/** One Sigma clock on a case (DoorSlaClockRead). */
export interface DoorSlaClockRead {
  clock: SlaClockKey;
  /** The Sigma stage (1 issue, 2 solution, 3 effectiveness, 4 customer). */
  stage: number;
  label: string;
  status: SlaClockStatus;
  due_at: string | null;
  done_at: string | null;
  /** due_at is estimated off an earlier, unmet clock. */
  projected: boolean;
  /** Whole days to the deadline (negative once past); null when not running. */
  days_remaining: number | null;
  /**
   * "How this is decided" (kanban 823ec064): what the stage means, what
   * stamped it (null while running) and the plain-language rule / basis.
   * Optional: an older server omits them and the panel prints less.
   */
  meaning?: string;
  decided_by?: SlaDecidedBy | null;
  how?: string;
  /** Can a person mark / edit / clear this stage by hand (stage 3 cannot). */
  markable?: boolean;
  /** Who last marked or edited it by hand (or Merlin from chat), and when. */
  recorded_by?: string | null;
  recorded_at?: string | null;
  recorded_in_chat?: boolean;
  /**
   * Stage 3 only (kanban 3a8f10ca): shipped invoices of the product to the
   * customer since completion (null when not counted) and the count that
   * closes the watch — "90 days or 10 invoices, whichever comes first".
   */
  invoices_seen?: number | null;
  invoices_target?: number | null;
}

export type SlaDecidedBy =
  | 'case_opened'
  | 'transition'
  | 'stamp'
  | 'dated_items'
  | 'case_completed'
  | 'watch';

export type EffectivenessOutcome = 'held' | 'failed';

/**
 * The case on the OTHER end of a resurrection link (DoorInvestigationLinkRead):
 * on a new case these are the old, completed cases it resurrects; on an old
 * case, the later cases that rediscovered it.
 */
export interface DoorInvestigationLinkRead {
  investigation_id: string;
  investigation_no: string;
  title: string;
  status: InvestigationStatus;
  /** Why the linker judged them the same underlying issue. */
  note: string | null;
  created_at: string;
}

/** An investigation as returned by the door router (DoorInvestigationRead). */
/**
 * A formal complaint behind a case, as the sales team entered it on the
 * sales-site product feedback form (mirrored into quality_issues; form-only
 * fields come from the sync's audit trail). `link_source`: the promoting
 * thread cited it ('thread') or a LEG/QI number sits in the case keys
 * ('bridge_key').
 */
export interface DoorCaseIssueRead {
  id: string;
  issue_no: string;
  title: string;
  status: string;
  category: string;
  severity: string;
  source: string;
  link_source: 'thread' | 'bridge_key';
  description: string;
  resolution: string | null;
  csr_contact: string | null;
  customer_no: string | null;
  root_cause: string | null;
  root_cause_category: string | null;
  root_cause_subcategory: string | null;
  department_responsible: string | null;
  assigned_to: string | null;
  lot_number: string | null;
  po_number: string | null;
  cost_of_damages: string | null;
  product_skus: string[];
  attachment_count: number;
  legacy_feedback_id: number | null;
  occurred_at: string | null;
  resolved_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  tags: string[];
  /** The complaint lives on the sales site and is not resolved there — the
   *  portal cannot tick it yet, so completing the case leaves it open
   *  (optional: older backends omit it). */
  external_closure_pending?: boolean;
}

/**
 * One Merlin conversation attached to a case (DoorInvestigationConversationRead)
 * — the case file's "Linked conversations" rows. `title` is the chat drawer's
 * label; null when the conversation is untitled or its row is gone.
 */
export interface DoorInvestigationConversationRead {
  conversation_id: string;
  title: string | null;
  attached_at: string;
  /** The conversation the case was saved from; the rest were attached later. */
  is_originating: boolean;
}

/** theirs: an open ask is out to a person; ours: none is; ready: marked
 *  ready to close with nothing out; watching: completed on our side, the
 *  Sigma effectiveness window still open (kanban d91f705b); done: cancelled,
 *  or completed with the window ended. */
export type CourtSide = 'theirs' | 'ours' | 'ready' | 'watching' | 'done';

/** A case's court (services/investigations/court.py). On `theirs` the
 *  person and dates are the oldest open ask's. */
export interface DoorCaseCourt {
  side: CourtSide;
  person: string | null;
  asked_at: string | null;
  next_reminder_at: string | null;
  open_asks: number;
  /** From the case's wait (kanban 88f93c9b): the one line the card shows,
   *  who asked them, the dates, and whether a person said so ('told') or
   *  Merlin judged it; 'ask' when the court comes from an open ask.
   *  Optional: an older backend sends none of these. */
  reason?: string | null;
  party?: 'customer' | 'colleague' | null;
  basis?: 'told' | 'judged' | 'ask' | 'watch' | null;
  via?: string | null;
  since?: string | null;
  expected_on?: string | null;
}

export type CaseWaitParty = 'customer' | 'colleague';
export type CaseWaitOutcome = 'received' | 'cleared';

/** The case's WAITING wait as a record (services/investigations/waits.py):
 *  what it waits on, from whom, since when, expected by when, who told us. */
export interface DoorCaseWait {
  id: string;
  state: 'waiting' | 'received' | 'cleared' | 'expired';
  party: CaseWaitParty;
  who_name: string;
  via_name: string | null;
  what_for: string;
  since: string | null;
  expected_on: string | null;
  told_by: string | null;
  reason: string;
  basis: 'told' | 'judged';
  set_by: string;
  set_at: string;
  followup_id: string | null;
  ended_at: string | null;
  ended_by: string | null;
  end_detail: Record<string, unknown> | null;
}

/** What Jess tells the case it is waiting on (POST .../wait). */
export interface CaseWaitInput {
  party: CaseWaitParty;
  who_name: string;
  what_for: string;
  via_name?: string | null;
  since?: string | null;
  expected_on?: string | null;
  reason?: string | null;
}

export interface DoorInvestigationRead {
  id: string;
  /** Human-facing case number, e.g. "INV-00042". */
  investigation_no: string;
  title: string;
  context_summary: string;
  status: InvestigationStatus;
  /** Whose court the ball is in (the 2026-08-20 Scott/Jess ask). */
  work_state: CaseWorkState;
  /** The work-state FSM's legal next states — buttons derive from this ONLY. */
  allowed_work_states: string[];
  findings: string;
  remediation_details: string[];
  resolution_summary: string | null;
  reopened_count: number;
  /**
   * Join keys the case is ABOUT (customer_no / sku / lot / order_no ...) —
   * same convention as the signal-thread router's bridge_keys; carried over
   * on thread promotion and editable on the case file. Metrics group by
   * these.
   */
  bridge_keys: Record<string, string>;
  originating_conversation_id: string | null;
  conversation_ids: string[];
  /** The same links with a printable title each, oldest-attached first
   *  (optional: older backends send only `conversation_ids`). */
  linked_conversations?: DoorInvestigationConversationRead[];
  /** Data requests under this investigation still waiting on an answer. */
  open_request_count: number;
  /** Answered requests whose answer the door user hasn't viewed yet. */
  unseen_response_count: number;
  oldest_open_request_at: string | null;
  /** Whose court, derived on the server from the case's open asks (proposal
   *  5). The board, the tab badge and the filters all read this, never
   *  `work_state`. */
  court: DoorCaseCourt;
  /** The WAITING wait behind a court from a wait; null (or absent on an
   *  older backend) otherwise. */
  wait?: DoorCaseWait | null;
  /** The case's outstanding-item checklist (cancelled items included). */
  tasks: DoorCaseTaskRead[];
  /** OPEN checklist items on our desk / the customer's desk. */
  open_task_count_us: number;
  open_task_count_customer: number;
  oldest_open_task_at: string | null;
  /** The FSM's legal next states — transition buttons derive from this ONLY. */
  allowed_transitions: string[];
  /**
   * Resurrection links, both directions (optional: older backends omit them).
   * `resurrects`: the completed cases THIS case rediscovers.
   * `resurrected_by`: later cases that rediscovered THIS case after we said
   * it was rectified. Links never change the old case's status.
   */
  resurrects?: DoorInvestigationLinkRead[];
  resurrected_by?: DoorInvestigationLinkRead[];
  /** The sales-site complaint(s) behind this case (optional: older backends
   *  omit it). Jess, 2026-09-08: "if it can pull all that information over". */
  linked_issues?: DoorCaseIssueRead[];
  /** LEG numbers still open on the sales site after this case (optional). */
  pending_external_closures?: string[];
  /** Scott's wording for the completed-case banner; null unless the case is
   *  completed and something is still pending on the sales site (optional). */
  external_closure_notice?: string | null;
  /** People's notes (notes round): the newest one and how many there are.
   *  Optional: older backends omit them. */
  latest_note?: DoorNoteRead | null;
  note_count?: number;
  /**
   * Sigma "Quality of Complaints" clocks (Jess Finlaw, QT-10056): the stamps
   * the clocks run from and one reading per clock in stage order. Optional:
   * older servers omit them, and the door then shows no clocks.
   */
  clock_started_at?: string | null;
  issue_identified_at?: string | null;
  root_cause_confirmed_at?: string | null;
  /** Stage 4a (kanban 3a8f10ca): the first word to the customer, via sales. */
  customer_acknowledged_at?: string | null;
  customer_response_sent_at?: string | null;
  customer_response_summary?: string | null;
  effectiveness_watch_until?: string | null;
  effectiveness_outcome?: EffectivenessOutcome | null;
  /** Stage 2's own record (kanban 823ec064): the corrective action in
   *  Jess's words, who owns it, and the explicit "mark complete" stamp. */
  corrective_action_summary?: string | null;
  corrective_action_owner?: string | null;
  corrective_action_completed_at?: string | null;
  clocks?: DoorSlaClockRead[];
  overdue_clock_count?: number;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

/** One step of a case's life — the case file's timeline entry. */
export interface DoorInvestigationEventRead {
  event_type: string;
  summary: string;
  actor_kind: 'merlin' | 'human' | 'system';
  created_at: string;
  /** Who, by name ("Jess Finlaw", "Merlin"). Older servers omit it. */
  actor_label?: string;
  /** The audit row's id (history pages). */
  event_id?: string;
  /** A person's note, verbatim (note_added rows). */
  note_text?: string | null;
}

/** A person's note on an item or a case, verbatim, under their name. */
export interface DoorNoteRead {
  id: string;
  text: string;
  author_label: string;
  created_at: string;
}

/**
 * One page of a history, newest first unless asked otherwise. `has_more`
 * says older rows exist; pass `next_cursor` back as `cursor` to get them.
 */
export interface HistoryPage<E> {
  events: E[];
  order: 'asc' | 'desc';
  has_more: boolean;
  next_cursor: string | null;
}

export interface HistoryQuery {
  cursor?: string | null;
  limit?: number;
}

/** How many audit rows one history page asks for. */
export const HISTORY_PAGE_SIZE = 100;

function historyParams(q: HistoryQuery = {}): Record<string, string | number> {
  const params: Record<string, string | number> = { order: 'desc', limit: q.limit ?? HISTORY_PAGE_SIZE };
  if (q.cursor) params.cursor = q.cursor;
  return params;
}

/** One page of a case's history, newest first. */
export async function getInvestigationHistory(
  investigationId: string,
  q: HistoryQuery = {},
): Promise<HistoryPage<DoorInvestigationEventRead>> {
  const { data } = await api.get<HistoryPage<DoorInvestigationEventRead>>(
    `${BASE}/investigations/${investigationId}/history`,
    { params: historyParams(q) },
  );
  return data;
}

/** People's notes on a case, newest first. */
export async function listInvestigationNotes(investigationId: string): Promise<DoorNoteRead[]> {
  const { data } = await api.get<DoorNoteRead[]>(`${BASE}/investigations/${investigationId}/notes`);
  return data;
}

/** Write a note on a case (422: empty / too long). */
export async function addInvestigationNote(investigationId: string, text: string): Promise<DoorNoteRead> {
  const { data } = await api.post<DoorNoteRead>(`${BASE}/investigations/${investigationId}/notes`, { text });
  return data;
}

/**
 * Mark a case as a resurrection of a previously-completed case. 400 = the
 * target isn't completed / self-link; 404 = unknown INV number.
 */
export async function linkInvestigationResurrection(
  investigationId: string,
  linkedInvestigationNo: string,
  note?: string,
): Promise<DoorInvestigationRead> {
  const { data } = await api.post<DoorInvestigationRead>(
    `${BASE}/investigations/${investigationId}/links`,
    { linked_investigation_no: linkedInvestigationNo, note: note || undefined },
  );
  return data;
}

/** One grouped count in a metrics dimension (e.g. key='CARRO09'). */
export interface DimensionSlice {
  key: string;
  count: number;
}

/** Cross-case aggregates (GET /investigations/metrics). */
export interface InvestigationMetrics {
  total: number;
  active: number;
  by_status: Record<string, number>;
  /** Active cases only — whose court the open work sits in. */
  by_work_state: Record<string, number>;
  /** Non-cancelled cases grouped by bridge_keys, worst-first, top 5. */
  by_customer: DimensionSlice[];
  by_sku: DimensionSlice[];
  /** Active cases with no bridge keys — invisible to the slices above. */
  untagged_active: number;
  reopened_cases: number;
  total_reopens: number;
  /** Cycle time across completed cases (created → completed), in days. */
  median_days_to_complete: number | null;
  open_asks: number;
  oldest_open_ask_at: string | null;
  open_tasks_us: number;
  open_tasks_customer: number;
}

/** Cross-case metrics for the panel's stats strip. */
export async function getInvestigationMetrics(): Promise<InvestigationMetrics> {
  const { data } = await api.get<InvestigationMetrics>(
    `${BASE}/investigations/metrics`,
  );
  return data;
}

/** List the door's investigations (newest first), optionally by status. */
export async function listInvestigations(
  statusFilter?: InvestigationStatus,
): Promise<DoorInvestigationRead[]> {
  const params: Record<string, string> = {};
  if (statusFilter) params.status_filter = statusFilter;
  const { data } = await api.get<DoorInvestigationRead[]>(
    `${BASE}/investigations`,
    { params },
  );
  return data;
}

/** One investigation with its ask/response aggregates. */
export async function getInvestigation(
  investigationId: string,
): Promise<DoorInvestigationRead> {
  const { data } = await api.get<DoorInvestigationRead>(
    `${BASE}/investigations/${investigationId}`,
  );
  return data;
}

/** One field of the sales-site packaging-feedback form, paste-ready. */
export interface DoorPackagingFeedbackSection {
  key: string;
  label: string;
  value: string;
  /** Where the value came from (e.g. "case findings", "sales-site form (already entered)"); "" when blank. */
  source: string;
  /** The form's pick-list when the field is a dropdown. */
  choices: string[];
}

/** The case rendered into the sales-site complaint form's sections, in the form's order. */
export interface DoorPackagingFeedbackRead {
  investigation_no: string;
  issue_no: string | null;
  sections: DoorPackagingFeedbackSection[];
  paste_all: string;
}

/** Paste-ready packaging-feedback sections for a case (read-only prep; nothing is written to the sales site). */
export async function getPackagingFeedbackExport(
  investigationId: string,
): Promise<DoorPackagingFeedbackRead> {
  const { data } = await api.get<DoorPackagingFeedbackRead>(
    `${BASE}/investigations/${investigationId}/packaging-feedback`,
  );
  return data;
}

/** The investigation a conversation belongs to, or null (chat-header pill). */
export async function investigationForConversation(
  conversationId: string,
): Promise<DoorInvestigationRead | null> {
  const { data } = await api.get<DoorInvestigationRead | null>(
    `${BASE}/investigations/for-conversation/${conversationId}`,
  );
  return data ?? null;
}

export interface InvestigationCreateBody {
  title: string;
  context_summary?: string;
  /** Save an existing conversation as the investigation's origin. */
  conversation_id?: string;
  /** Join keys the case is about (customer_no / sku / lot / order_no ...). */
  bridge_keys?: Record<string, string>;
}

/** Save a conversation as a new investigation (or start one bare). */
export async function createInvestigation(
  body: InvestigationCreateBody,
): Promise<DoorInvestigationRead> {
  const { data } = await api.post<DoorInvestigationRead>(
    `${BASE}/investigations`,
    body,
  );
  return data;
}

/**
 * Replace the case's join-key map (full replace; the server trims, drops
 * empties, and caps the map). 400 = a key/value over the size caps.
 */
/**
 * "This is not a quality case" — withdraw the case and hand the item behind
 * it to another team, with the reason (owner ruling, review bz2bb6).
 * 409 when the case was saved from a chat and has no item behind it.
 */
export async function handOffInvestigation(
  investigationId: string,
  routeTo: string,
  note: string,
): Promise<CaseHandOffResponse> {
  const { data } = await api.post<CaseHandOffResponse>(
    `${BASE}/investigations/${investigationId}/hand-off`,
    { route_to: routeTo, note },
  );
  return data;
}

export async function setInvestigationBridgeKeys(
  investigationId: string,
  bridgeKeys: Record<string, string>,
): Promise<DoorInvestigationRead> {
  const { data } = await api.post<DoorInvestigationRead>(
    `${BASE}/investigations/${investigationId}/bridge-keys`,
    { bridge_keys: bridgeKeys },
  );
  return data;
}

/** Attach another conversation (the investigation adopts its data requests). */
export async function attachInvestigationConversation(
  investigationId: string,
  conversationId: string,
): Promise<DoorInvestigationRead> {
  const { data } = await api.post<DoorInvestigationRead>(
    `${BASE}/investigations/${investigationId}/attach-conversation`,
    { conversation_id: conversationId },
  );
  return data;
}

/**
 * Take a conversation back off a case (the save dialog's Undo). The case
 * releases the asks it held from that chat. 409 for the conversation the
 * case was saved from; 404 when it is not attached.
 */
export async function detachInvestigationConversation(
  investigationId: string,
  conversationId: string,
): Promise<DoorInvestigationRead> {
  const { data } = await api.delete<DoorInvestigationRead>(
    `${BASE}/investigations/${investigationId}/conversations/${conversationId}`,
  );
  return data;
}

export interface InvestigationTransitionBody {
  to_status: InvestigationStatus;
  findings?: string;
  remediation_details?: string[];
  resolution_summary?: string;
  reopen_note?: string;
}

/**
 * Walk the FSM. 400 = gate refusal (e.g. findings required before
 * Remediating) — show the server's detail; 409 = illegal transition.
 */
export async function transitionInvestigation(
  investigationId: string,
  body: InvestigationTransitionBody,
): Promise<DoorInvestigationRead> {
  const { data } = await api.post<DoorInvestigationRead>(
    `${BASE}/investigations/${investigationId}/transition`,
    body,
  );
  return data;
}

/**
 * Record what the case is waiting on (kanban 88f93c9b): the court follows
 * it, and an expected date arms a Merlin follow-up. Idempotent on the same
 * wait; 409 on a finished case.
 */
export async function setInvestigationWait(
  investigationId: string,
  wait: CaseWaitInput,
): Promise<DoorInvestigationRead> {
  const { data } = await api.post<DoorInvestigationRead>(
    `${BASE}/investigations/${investigationId}/wait`,
    {
      party: wait.party,
      who_name: wait.who_name,
      what_for: wait.what_for,
      via_name: wait.via_name || null,
      since: wait.since || null,
      expected_on: wait.expected_on || null,
      reason: wait.reason || null,
    },
  );
  return data;
}

/** End the case's wait: it arrived (received) or we are not waiting after
 *  all (cleared). 409 when the case waits on nobody. */
export async function endInvestigationWait(
  investigationId: string,
  outcome: CaseWaitOutcome,
  note?: string,
): Promise<DoorInvestigationRead> {
  const { data } = await api.post<DoorInvestigationRead>(
    `${BASE}/investigations/${investigationId}/wait/end`,
    { outcome, note: note || undefined },
  );
  return data;
}

/**
 * Flip whose court the ball is in. 409 = illegal (no-op flip or a stale
 * panel); 400 = unknown state.
 */
export async function setInvestigationWorkState(
  investigationId: string,
  toState: CaseWorkState,
  note?: string,
): Promise<DoorInvestigationRead> {
  const { data } = await api.post<DoorInvestigationRead>(
    `${BASE}/investigations/${investigationId}/work-state`,
    { to_state: toState, note: note || undefined },
  );
  return data;
}

/** Append text to a case's findings (never replacing). With `requestId`
 *  the stamp says who answered. 400 on a closed case. */
export async function appendCaseFindings(
  investigationId: string,
  text: string,
  requestId: string | null = null,
): Promise<DoorInvestigationRead> {
  const { data } = await api.post<DoorInvestigationRead>(
    `${BASE}/investigations/${investigationId}/findings`,
    { text, data_request_id: requestId },
  );
  return data;
}

/** Sigma stage 2 on a checklist item: who, by name, and by when. */
export interface CaseTaskAssignment {
  owner_name?: string | null;
  /** YYYY-MM-DD. */
  due_at?: string | null;
}

/** Add an outstanding item to the case's checklist (ours or customer's),
 *  optionally with its named owner and completion date. */
export async function addInvestigationTask(
  investigationId: string,
  label: string,
  owner: CaseTaskOwner,
  assignment: CaseTaskAssignment = {},
): Promise<DoorInvestigationRead> {
  const body: Record<string, unknown> = { label, owner };
  if (assignment.owner_name) body.owner_name = assignment.owner_name;
  if (assignment.due_at) body.due_at = assignment.due_at;
  const { data } = await api.post<DoorInvestigationRead>(
    `${BASE}/investigations/${investigationId}/tasks`,
    body,
  );
  return data;
}

/** Walk a checklist item's FSM (check off / uncheck / cancel / restore). */
export async function transitionInvestigationTask(
  investigationId: string,
  taskId: string,
  toStatus: CaseTaskStatus,
  /** ``note``: Jess's words on a removal (kanban 59a1ee04); ignored otherwise. */
  opts: { note?: string | null } = {},
): Promise<DoorInvestigationRead> {
  const { data } = await api.post<DoorInvestigationRead>(
    `${BASE}/investigations/${investigationId}/tasks/${taskId}/status`,
    { to_status: toStatus, ...(opts.note ? { note: opts.note } : {}) },
  );
  return data;
}

/** What an item edit may change: its wording, its desk, and (Sigma stage
 *  2) its named owner / completion date — `clear_*` unsets those. */
export interface CaseTaskEdit {
  label?: string;
  owner?: CaseTaskOwner;
  owner_name?: string;
  clear_owner_name?: boolean;
  /** YYYY-MM-DD. */
  due_at?: string;
  clear_due_at?: boolean;
  /** How a customer-owed step reaches the customer, in Jess's words;
   *  `clear_recommendation` restores the default (kanban d3915b86). */
  recommendation?: string;
  clear_recommendation?: boolean;
}

/** Edit a checklist item's label, desk, named owner, completion date, or
 *  how a customer step reaches the customer. */
export async function editInvestigationTask(
  investigationId: string,
  taskId: string,
  changes: CaseTaskEdit,
): Promise<DoorInvestigationRead> {
  const { data } = await api.post<DoorInvestigationRead>(
    `${BASE}/investigations/${investigationId}/tasks/${taskId}/edit`,
    changes,
  );
  return data;
}

/** Sigma stage 4: record that the customer got their response (the response
 *  itself goes out by email / phone; this stamps when and summarises what). */
export async function recordCustomerResponse(
  investigationId: string,
  summary: string,
  sentAt?: string | null,
): Promise<DoorInvestigationRead> {
  const body: Record<string, unknown> = { summary };
  if (sentAt) body.sent_at = sentAt;
  const { data } = await api.post<DoorInvestigationRead>(
    `${BASE}/investigations/${investigationId}/customer-response`,
    body,
  );
  return data;
}

/** Mark a Sigma stage complete, edit its record, or remove its stamp
 *  (kanban 823ec064). ``completed_at`` is an ISO datetime (omit = now);
 *  ``summary`` / ``owner_name``: undefined leaves as is, '' clears. */
export interface StageRecordBody {
  mark_complete?: boolean;
  completed_at?: string;
  clear_completed?: boolean;
  summary?: string;
  owner_name?: string;
}

/** POST .../stages/{clock}: the same write Merlin's case_file_mark_stage
 *  makes from chat, through services.investigations.record_stage. */
export async function recordInvestigationStage(
  investigationId: string,
  clock: SlaClockKey,
  body: StageRecordBody,
): Promise<DoorInvestigationRead> {
  const { data } = await api.post<DoorInvestigationRead>(
    `${BASE}/investigations/${investigationId}/stages/${clock}`,
    body,
  );
  return data;
}

/* ------------------------------------------------------------------ */
/* Tracked signal threads (Merlin's proactive routing)                 */
/* ------------------------------------------------------------------ */

export type ThreadStatus =
  | 'open'
  | 'routed'
  | 'in_investigation'
  | 'dismissed'
  /** A legitimate issue, handled without a case file — NOT "not an issue". */
  | 'completed';

export type ThreadSourceTag = 'sales_site' | 'signal_suspected';

/** Thread posture (signal_threads.work_state; CHECK-constrained server-side). */
export type ThreadWorkState =
  | 'active'
  | 'escalating'
  | 'de_escalated'
  | 'awaiting_evidence'
  | 'on_hold';

/** A Merlin-managed quality thread routed to a person (DoorThreadRead). */
export interface DoorThreadRead {
  id: string;
  /** Human-facing number, e.g. "QT-00007". */
  thread_no: string;
  title: string;
  /** Agent 1's "this is what I found" breakdown. */
  summary: string;
  recommended_action: string;
  status: ThreadStatus;
  /** sales_site = confirmed complaint; signal_suspected = "take a look". */
  source_tag: ThreadSourceTag;
  severity: 'watch' | 'high' | 'critical';
  /** Posture — distinct from status (lifecycle) and severity (how bad). */
  work_state: ThreadWorkState;
  /** The one-liner behind the posture ("contractor back next week with the lot"). */
  work_state_note: string;
  work_state_changed_at: string | null;
  /** kb_signals entry ids behind this thread. */
  signal_ids: string[];
  quality_issue_ids: string[];
  /** Join keys this thread is about (customer_no / sku / batch / order_no…) —
   *  how the router matches new evidence to it; the rail shows them as chips. */
  bridge_keys: Record<string, string>;
  routed_to: string | null;
  routing_reason: string | null;
  conversation_id: string | null;
  /** Whether the signed-in door user OWNS that conversation. The door scopes
   *  transcript reads and the composer to the routee, so false = the board
   *  must not fetch it (a non-owner would 404); null = no conversation;
   *  omitted = older backend, fetch as before. */
  conversation_owned_by_viewer?: boolean | null;
  investigation_id: string | null;
  dismissed_reason: string | null;
  /** What Merlin's investigation found — null until the investigator ran. */
  investigation: ThreadInvestigation | null;
  investigated_at: string | null;
  /** Merlin's proposed case file (optional: older backends omit it). */
  case_proposal?: ThreadCaseFileProposal | null;
  routed_at: string | null;
  resolved_at: string | null;
  /** Reopen bookkeeping (sthr07): set when a closed thread came back to the
   *  queue — by the router on recurrence, or by the recipient by hand. */
  reopened_at: string | null;
  reopened_count: number;
  /** What it was reopened FROM: 'completed' | 'dismissed'. */
  reopened_from_status: string | null;
  /** When it had been closed before the reopen ("you marked this Done on 9/2"). */
  last_closed_at: string | null;
  /** Merlin annotated this CLOSED thread without reopening it. */
  post_closure_update_at: string | null;
  /** People's notes (notes round): the newest one and how many there are.
   *  Optional: older backends omit them. */
  latest_note?: DoorNoteRead | null;
  note_count?: number;
  /** The newest reply to a hand-off email, recorded when the colleague the
   *  item was sent to answered it by email. Null when nobody has; omitted by
   *  older backends. */
  latest_route_reply?: DoorRouteReplyRead | null;
  /** "Waiting on a person" as a record (card a095e349): the item's newest
   *  wait in any state; null when it never had one (then a legacy "Waiting
   *  on …" posture note may still say so); omitted by older backends. */
  wait?: DoorThreadWaitRead | null;
  /** Was the hand-off email for the CURRENT route ever sent, from the
   *  route email audit (card 75bf026f)? 'not_sent' = Merlin assigned it by
   *  raw SQL and nobody was emailed: the people named do not know they hold
   *  it. null = no email expected (with you, unrouted, closed, ruled a
   *  duplicate); omitted by older backends. */
  route_notified?: RouteNotified | null;
  /**
   * Where the current route came from: who assigned it, by which path, when
   * and from whom (review 2r8pgd). Null when the item is not live with
   * someone other than Jess or the audit holds no line that explains it;
   * absent on an older backend.
   */
  route_origin?: RouteOrigin | null;
  /** The door gate's ledger for the current route (kanban 8a4d2212); empty
   *  when the gate is off or the route came by another path. */
  handoffs?: DoorHandoffRead[];
  /** Why the item is with its routee, as the rules Merlin applied in order
   *  (kanban 143454d3): the one-line summary for the folded "Why it came
   *  to you" and the rules for the expanded view. Null when nothing routed
   *  it; absent on an older backend. */
  routing_rationale?: RoutingRationale | null;
  /** The newest nudge Jess sent the person this item waits on (kanban
   *  68cc2cfb); null when never nudged; omitted by older backends. */
  last_nudge?: DoorThreadNudgeRead | null;
  created_at: string;
  updated_at: string;
}

/** One rule in the order Merlin applies them (services/signal_threads/
 *  routing_rationale.py). `applied` = this line decided the route. */
export interface RoutingRule {
  kind: 'human' | 'default' | 'instruction' | 'classification';
  applied: boolean;
  text: string;
  /** "RI-1a2b3c4d" for a recorded instruction line; null for the count line. */
  instruction_id?: string | null;
  recorded_by?: string | null;
  recorded_at?: string | null;
}

export interface RoutingRationale {
  summary: string;
  rules: RoutingRule[];
  /** Active recorded instructions on file when this was read. */
  instructions_on_file: number;
}

/** How a nudge went out: a reminder on the person's open ask, or a new ask
 *  worded as the nudge (for a wait record, or a delivered hand-off). */
export type NudgeVia = 'ask_reminder' | 'wait_ask' | 'handoff_ask';

export interface DoorThreadNudgeRead {
  at: string;
  who: string;
  via: NudgeVia;
  by_label: string;
  request_ids: string[];
}

/** sent = at least one of the people it is with now got the hand-off
 *  email; failed = nobody was reached and the newest word is a failed
 *  send; not_sent = no send about them was ever attempted. */
/** 'pending' / 'held' (kanban 8a4d2212): the door gate is holding the
 *  hand-off for Merlin's "already known?" verdict, or Merlin held it as
 *  already known — not emailed by design, never "never emailed". */
export type RouteNotified = 'sent' | 'not_sent' | 'failed' | 'pending' | 'held';

/** One recipient's hand-off as the door gate's ledger holds it (kanban 8a4d2212). */
export interface DoorHandoffRead {
  sweep_item_id: string;
  to_name: string;
  to_email: string;
  /** pending = waiting for Merlin's check; held = Merlin judged they already
   *  know (Send anyway offered); sent / failed = the email's fate. */
  state: 'pending' | 'held' | 'sent' | 'failed' | 'superseded';
  mode: string;
  verdict?: string | null;
  recommendation?: string | null;
  /** What Merlin found they already have (a hold's case). */
  known_points?: string[];
  note?: string | null;
  detail?: string | null;
  curated_at?: string | null;
  updated_at: string;
}

export type RouteVia =
  | 'quality_door'
  | 'admin_routing_page'
  | 'nightly_router'
  | 'merlin'
  | 'database'
  | 'other';

export interface RouteOrigin {
  via: RouteVia;
  actor_kind: 'merlin' | 'human' | 'system';
  /** Who, as the door names people: a person's name, or Merlin's router. */
  actor_label: string;
  at: string | null;
  /** Who had it before this route; null when nobody did. */
  previous_routee: string | null;
  /** The item was created already assigned to these people. */
  born_routed: boolean;
}

/** What kind of thing an evidence ref names (kanban 1af16b1c). */
export type ThreadEvidenceKind =
  | 'order'
  | 'batch'
  | 'fill'
  | 'po'
  | 'lot'
  | 'sku'
  | 'signal'
  | 'email'
  | 'issue'
  | 'case'
  | 'customer'
  | 'source'
  | 'other';

/** One identifier Merlin read for a fact or a next step (kanban 1af16b1c):
 *  the number or id as written, what it is, and what it showed. */
export interface ThreadEvidenceRef {
  ref: string;
  kind: ThreadEvidenceKind | string;
  note: string;
}

/** Why one recommended-action item (kanban 1af16b1c): `step` is its
 *  1-based number in the filed list, `basis` what Merlin checked that
 *  makes it the step, `evidence` the identifiers it read for it. */
export interface ThreadStepEvidence {
  step: number;
  basis: string;
  evidence: ThreadEvidenceRef[];
}

/** One fact of the situation and the source Merlin checked it in — the
 *  brief shows the fact and opens the basis on a click. */
export interface ThreadKeyFact {
  fact: string;
  basis: string;
  /** The identifiers Merlin read for it (kanban 1af16b1c). Filings from
   *  before it carry none. */
  evidence?: ThreadEvidenceRef[];
  /** What the brief's tile shows (kanban 538d6a0d): the topic in a word or
   *  two, the number or state in a few words, and how that fact looks today.
   *  Filings from before the tiles have none of the three. */
  label?: string;
  headline?: string;
  status?: 'problem' | 'watch' | 'ok' | '';
}

/** Merlin's structured filing for a thread (services/agent_console/tools/thread_investigation.py). */
/** One link of Merlin's cause chain (kanban 303c204a). `fact` and `drives`
 *  are 1-based numbers into `key_facts` and the recommended-action list. */
export interface ThreadCauseStep {
  when: string;
  step: string;
  detail: string;
  state: 'past' | 'now' | 'next';
  fact: number | null;
  drives: number[];
}

export interface ThreadInvestigation {
  /** The actual issue in one or two sentences (kanban 538d6a0d, Jess
   *  2026-10-05). Older filings have none; `at_a_glance` then leads. */
  issue?: string;
  at_a_glance: string;
  traced: string;
  could_not_confirm: string;
  /** Two or three facts about the situation (dates, quantities, what the
   *  customer said), each with its basis. Optional: older filings omit it,
   *  and the first day's filings (2026-10-01) wrote a markdown-bullet string. */
  key_facts?: string | ThreadKeyFact[];
  /** How we got here, oldest first. Filings before the chain have none. */
  cause_chain?: ThreadCauseStep[];
  /** Why each recommended-action item, by number (kanban 1af16b1c).
   *  Filings before it have none and the Do next list opens nothing. */
  step_evidence?: ThreadStepEvidence[];
  checked: string[];
  bridge_keys: Record<string, string>;
  /** The next steps; newer filings write a numbered markdown list. */
  recommended_action: string;
  suggested_owner: string;
  severity: string;
  confidence: number | null;
  /** INV numbers of completed cases this thread rediscovers (optional:
   *  older filings omit these fields). */
  resurrection_of?: string[];
  resurrection_note?: string;
}

/** One outstanding step Merlin proposes — same shape as a case task. */
export interface ThreadCaseItemProposal {
  id: string;
  label: string;
  owner: CaseTaskOwner;
  status: CaseTaskStatus;
}

/**
 * Merlin's PROPOSED case file, kept on the thread before any case exists
 * (written through its case_file_* tools; seeds the INV when the recipient
 * opens the case). Empty collections / '' when nothing is proposed.
 */
export interface ThreadCaseFileProposal {
  items: ThreadCaseItemProposal[];
  current_state: string;
  whose_court: CaseWorkState | '';
  answered_questions: Array<{ question: string; answer: string }>;
  findings_notes: string[];
  updated_at: string | null;
  updated_by: string | null;
}

/** One kb_signals entry behind a thread — the board's evidence rail card. */
export interface DoorThreadSignalRead {
  id: string;
  observation: string;
  significance: string;
  source: string;
  accounts: string[];
  products: string[];
  created_at: string;
}

/** One formal complaint behind a thread — LEG/QI number + title, not a UUID. */
export interface DoorThreadIssueRead {
  id: string;
  issue_no: string;
  title: string;
  status: string;
  created_at: string;
  /** Sales-site complaint not resolved over there — a Done thread still
   *  leaves it open (optional: older backends omit it). */
  external_closure_pending?: boolean;
}

export interface DoorThreadSignalsResponse {
  signals: DoorThreadSignalRead[];
  /** Ids the bank no longer returns — listed bare so the count stays honest. */
  missing: string[];
  /** quality_issue_ids resolved to readable issue cards (optional: older
   *  backends omit these fields). */
  issues?: DoorThreadIssueRead[];
  /** Issue ids that didn't resolve — listed bare, same honesty rule. */
  missing_issues?: string[];
}

/** An email from the person the item is with: who, when, the first line,
 *  and how it was tied to the item — a reply on the hand-off's own email
 *  thread, or mail about the item that named it (its number, an order or
 *  customer ref — the refs that matched are listed). Older servers send
 *  neither field: read that as a hand-off reply. */
export interface DoorRouteReplyRead {
  from_name: string;
  from_email: string;
  received_at: string | null;
  excerpt: string;
  kind?: 'hand_off_reply' | 'about_item';
  matched_refs?: string[];
}

/** One step of a thread's life — the board's timeline entry. */
export interface DoorThreadEventRead {
  event_type: string;
  summary: string;
  actor_kind: 'merlin' | 'human' | 'system';
  created_at: string;
  /** Who, by name ("Jess Finlaw", "Merlin"). Older servers omit it. */
  actor_label?: string;
  /** The audit row's id (history pages). */
  event_id?: string;
  /** A person's note, verbatim (note_added rows). */
  note_text?: string | null;
  /** The colleague's email on the item (route_reply_received rows). */
  reply?: DoorRouteReplyRead | null;
}

export interface ThreadOpenInvestigationResponse {
  thread: DoorThreadRead;
  investigation: DoorInvestigationRead;
}

/** Merlin's tracked threads (most recently touched first). Defaults to the
 *  routed set; pass e.g. 'all' or 'routed,dismissed' for the board's lanes. */
export async function listThreads(statusFilter?: string): Promise<DoorThreadRead[]> {
  const { data } = await api.get<DoorThreadRead[]>(`${BASE}/threads`, {
    params: statusFilter ? { status_filter: statusFilter } : undefined,
  });
  return data;
}

/** This viewer's opens of one item (card 10a54f95). `previous_seen_at` is
 *  the open before the one just recorded: what "since you last looked" means
 *  for this visit. Null on a first look. */
export interface DoorThreadSeenRead {
  seen_at: string | null;
  previous_seen_at: string | null;
  view_count: number;
}

/** Record that the viewer opened this item; the answer carries the open
 *  before it. One row per open, server-side, so the mark follows the person
 *  across browsers (the localStorage seen-marks do not). */
export async function recordThreadView(threadId: string): Promise<DoorThreadSeenRead> {
  const { data } = await api.post<DoorThreadSeenRead>(`${BASE}/threads/${threadId}/seen`);
  return data;
}

/** Open a birth-linked INV case file from a routed thread. */
/** The evidence behind one thread, fetched lazily when the board opens it. */
export async function listThreadSignals(
  threadId: string,
): Promise<DoorThreadSignalsResponse> {
  const { data } = await api.get<DoorThreadSignalsResponse>(
    `${BASE}/threads/${threadId}/signals`,
  );
  return data;
}

/** One page of a thread's history, newest first (the door reads this, not
 *  /timeline, which returns only the OLDEST 50 rows). */
export async function getThreadHistory(
  threadId: string,
  q: HistoryQuery = {},
): Promise<HistoryPage<DoorThreadEventRead>> {
  const { data } = await api.get<HistoryPage<DoorThreadEventRead>>(
    `${BASE}/threads/${threadId}/history`,
    { params: historyParams(q) },
  );
  return data;
}

/** People's notes on a thread, newest first. */
export async function listThreadNotes(threadId: string): Promise<DoorNoteRead[]> {
  const { data } = await api.get<DoorNoteRead[]>(`${BASE}/threads/${threadId}/notes`);
  return data;
}

/** Write a note on a thread (422: empty / too long). */
export async function addThreadNote(threadId: string, text: string): Promise<DoorNoteRead> {
  const { data } = await api.post<DoorNoteRead>(`${BASE}/threads/${threadId}/notes`, { text });
  return data;
}

export async function openThreadInvestigation(
  threadId: string,
): Promise<ThreadOpenInvestigationResponse> {
  const { data } = await api.post<ThreadOpenInvestigationResponse>(
    `${BASE}/threads/${threadId}/open-investigation`,
    {},
  );
  return data;
}

/** One staff member the route-away picker can offer. */
export interface DoorRouteTargetRead {
  name: string;
  email: string | null;
}

/** The generic roles the picker can offer instead of a name. */
export type RouteRole = 'sales_rep' | 'cs_rep';

/**
 * A ROLE resolved for one thread's account — "Account's sales rep → Cliff
 * Platt". `available` is false (with `reason`) when the account has nobody
 * on file for it; the picker greys those out.
 */
export interface DoorRoleTargetRead {
  kind: 'role';
  role: RouteRole;
  /** Stable token to send in `route_to` (e.g. `role:sales_rep`). */
  token: string;
  label: string;
  customer_no: string | null;
  resolved_name: string | null;
  resolved_email: string | null;
  /** How the data got there ("via DO Dornseif"). */
  detail: string | null;
  available: boolean;
  reason: string | null;
}

/** Everything the picker can offer for ONE thread: roles first, then staff. */
export interface DoorThreadRouteTargetsRead {
  roles: DoorRoleTargetRead[];
  staff: DoorRouteTargetRead[];
}

/** What the route-away handoff actually did — shown to the sender. */
export interface DoorRouteDeliveryRead {
  /** The handoff email went out to these people. */
  emailed: Array<{
    name: string;
    email: string;
    sent_email_id: string;
    role?: RouteRole | null;
    role_label?: string | null;
  }>;
  /** Typed text that matched nobody (or several people) — NOT emailed. */
  unresolved: Array<{
    segment: string;
    candidates: string[];
    role?: RouteRole | null;
    reason?: string | null;
  }>;
  /** Resolved but the send failed (or no email on file). */
  failed: Array<{ name: string; email: string | null; error: string }>;
  /** The coverage gate withheld the email: these alerts already cover the item. */
  covered_by?: string[];
  /** The sender routed it to themselves (Pull back): in their queue, nobody emailed. */
  self_routed?: Array<{ name: string; email: string }>;
  /** Door gate, live (kanban 8a4d2212): the email waits for Merlin's
   *  "already known?" verdict on these people. */
  pending?: Array<{
    name: string;
    email: string;
    role?: RouteRole | null;
    role_label?: string | null;
    sweep_item_id: string;
  }>;
}

export interface ThreadRerouteResponse {
  thread: DoorThreadRead;
  delivery: DoorRouteDeliveryRead;
}

/** A hand-off answers with the cancelled case too, so the pane Jess is left
 *  looking at stops showing the stage it was in before he handed it on. */
export interface CaseHandOffResponse extends ThreadRerouteResponse {
  investigation: DoorInvestigationRead;
}

/**
 * Who THIS thread can be sent to: the account's sales rep / CS rep resolved
 * from Merlin's data for the thread's account, plus the staff directory the
 * by-name path resolves against.
 */
export async function listThreadRouteTargets(
  threadId: string,
): Promise<DoorThreadRouteTargetsRead> {
  const { data } = await api.get<DoorThreadRouteTargetsRead>(
    `${BASE}/threads/${threadId}/route-targets`,
  );
  return data;
}

export interface ThreadNudgeResponse {
  thread: DoorThreadRead;
  /** The ask rows the nudge rode on: the reminded asks, or the new one. */
  requests: DoorDataRequestRead[];
  via: NudgeVia;
  who: string;
  nudged_at: string;
}

/**
 * Nudge the person this item waits on (kanban 68cc2cfb): Merlin sends them
 * a new message saying Jess asked and is still waiting, through the ask
 * path. 409 carries why it was refused, in words to show (a hand-off Merlin
 * has not released, nobody waiting, nudged minutes ago); 400 when the
 * person cannot be reached.
 */
export async function nudgeThread(threadId: string, who: string): Promise<ThreadNudgeResponse> {
  const { data } = await api.post<ThreadNudgeResponse>(`${BASE}/threads/${threadId}/nudge`, { who });
  return data;
}

/**
 * "This should go to someone else" — reroutes, teaches the router, and
 * actually delivers the handoff (email). The response says what went out.
 */
/**
 * Options a decision can carry (kanban 143454d3). `standingRule` makes the
 * note a RECORDED routing instruction — "items like this go to them because
 * …" — that Merlin applies on the next similar item and Scott can retire.
 */
export interface DecisionOptions {
  standingRule?: boolean;
}

export async function rerouteThread(
  threadId: string,
  routeTo: string,
  note?: string,
  options: DecisionOptions = {},
): Promise<ThreadRerouteResponse> {
  const { data } = await api.post<ThreadRerouteResponse>(
    `${BASE}/threads/${threadId}/reroute`,
    {
      route_to: routeTo,
      note: note || undefined,
      standing_rule: options.standingRule ? true : undefined,
    },
  );
  return data;
}

/**
 * Send the hand-off email now, past Merlin's "already known" hold or before
 * its verdict (door gate, kanban 8a4d2212). Answers like a reroute: what
 * went out, and the thread with its hand-off states refreshed.
 */
export async function sendHandoffAnyway(
  threadId: string,
  toEmail: string,
): Promise<ThreadRerouteResponse> {
  const { data } = await api.post<ThreadRerouteResponse>(
    `${BASE}/threads/${threadId}/handoffs/send-anyway`,
    { to_email: toEmail },
  );
  return data;
}

/** Done — a legitimate issue, handled; one click, note optional. Distinct
 *  from dismissThread so real issues are never logged as non-issues. */
export async function completeThread(
  threadId: string,
  note?: string,
): Promise<DoorThreadRead> {
  const { data } = await api.post<DoorThreadRead>(
    `${BASE}/threads/${threadId}/complete`,
    { note: note || undefined },
  );
  return data;
}

/** Not an issue — the reason calibrates the router's playbook. */
/** Reopen a Done/Dismissed thread by hand — back into the queue, same
 *  audit trail as the router's recurrence reopen. */
export async function reopenThread(
  threadId: string,
  note?: string,
  routeTo?: string,
): Promise<DoorThreadRead> {
  const { data } = await api.post<DoorThreadRead>(
    `${BASE}/threads/${threadId}/reopen`,
    { note: note ?? null, route_to: routeTo ?? null },
  );
  return data;
}

export async function dismissThread(
  threadId: string,
  reason: string,
  options: DecisionOptions = {},
): Promise<DoorThreadRead> {
  const { data } = await api.post<DoorThreadRead>(
    `${BASE}/threads/${threadId}/dismiss`,
    { reason, standing_rule: options.standingRule ? true : undefined },
  );
  return data;
}

/** Set the thread's posture (de-escalated, awaiting evidence, …). */
/** The states of a wait (services/signal_threads/waits.py). */
export type ThreadWaitState = 'waiting' | 'answered' | 'acknowledged' | 'cleared' | 'expired';

/** One structured wait on an item. */
export interface DoorThreadWaitRead {
  id: string;
  state: ThreadWaitState;
  who_name: string;
  who_staff_id: number | null;
  who_email: string | null;
  /** yyyy-mm-dd, or null. */
  until: string | null;
  reason: string;
  set_by_label: string;
  set_at: string;
  ended_at: string | null;
  /** 'email' | 'ask' | 'item' (answered), 'hand' | 'replaced', 'expired'. */
  ended_via: string | null;
  /** Who the recorded response came from (answered waits). */
  answered_by: string | null;
  acknowledged_at: string | null;
}

export interface ThreadWaitBody {
  who_name: string;
  /** When picked from the staff directory: pins the person. */
  who_email?: string | null;
  until?: string | null;
  reason?: string | null;
}

/** Mark the item as waiting on a person (a repeat with the same values
 *  changes nothing). */
export async function setThreadWait(threadId: string, body: ThreadWaitBody): Promise<DoorThreadRead> {
  const { data } = await api.post<DoorThreadRead>(`${BASE}/threads/${threadId}/wait`, body);
  return data;
}

/** Stop waiting by hand. */
export async function clearThreadWait(threadId: string, note?: string): Promise<DoorThreadRead> {
  const { data } = await api.post<DoorThreadRead>(`${BASE}/threads/${threadId}/wait/clear`, {
    note: note || undefined,
  });
  return data;
}

/** The answer that ended the wait has been seen. */
export async function acknowledgeThreadWait(threadId: string): Promise<DoorThreadRead> {
  const { data } = await api.post<DoorThreadRead>(`${BASE}/threads/${threadId}/wait/acknowledge`, {});
  return data;
}

export async function setThreadWorkState(
  threadId: string,
  toState: ThreadWorkState,
  note?: string,
): Promise<DoorThreadRead> {
  const { data } = await api.post<DoorThreadRead>(
    `${BASE}/threads/${threadId}/work-state`,
    { to_state: toState, note: note || undefined },
  );
  return data;
}
